/**
 * The event-refresh (moo-4794) job vocabulary and the readers for a refresh job row.
 *
 * `@/lib/tina/content-types` owns the `music_event_refresh` **content type**; `@/types/tina` owns
 * the wire shapes (`RefreshJobRow`, `RefreshReviewProblem`, `RefreshReviewResolution`). This
 * module owns everything a curator surface needs on top of them: the labels, the outcome tones,
 * the projections of the `metadata.refresh` jsonb block, and the one deep link into Mood.
 *
 * It is the twin of `@/lib/tina/enrichment-jobs`, and follows the same two naming families:
 *
 * - `read*` projects an untyped jsonb block off a `PipelineItem`, through the
 *   `asRecord`/`asString`/`asBoolean` readers, so a malformed row degrades to `null` instead of
 *   throwing inside a table cell. Those view-models stay **snake_case**, matching the wire.
 * - everything else takes an already-typed input and cannot fail.
 *
 * Every vocabulary value mirrors tina's `src/db/schemas/refresh.py` (`ChangeKind`, `RefreshMode`)
 * or `src/db/queries/refresh_state.py` (`RefreshOutcome`). The label maps are typed
 * `Record<Union, string>`, so adding a member to a tuple in `@/types/tina` without labelling it
 * here is a compile error rather than a silently blank cell.
 */
import { isRefreshContentType } from "@/lib/tina/content-types";
import {
  asBoolean,
  asRecord,
  asString,
  asStringArray,
} from "@/lib/tina/value-readers";
import {
  REFRESH_CHANGE_KINDS,
  REFRESH_MODES,
  REFRESH_OUTCOMES,
  REFRESH_PAGE_GONE_ACTIONS,
  REFRESH_PAGE_GONE_REASONS,
  type RefreshChangeKind,
  type RefreshMode,
  type RefreshOutcome,
  type RefreshPageGoneAction,
  type RefreshPageGoneReason,
} from "@/types/tina";
import type {
  HitlContext,
  PipelineItem,
  ProposedChange,
  RefreshReviewProblem,
} from "@/types/tina";

export {
  REFRESH_CHANGE_KINDS,
  REFRESH_MODES,
  REFRESH_OUTCOMES,
  REFRESH_PAGE_GONE_ACTIONS,
  REFRESH_PAGE_GONE_REASONS,
};
export type {
  RefreshChangeKind,
  RefreshMode,
  RefreshOutcome,
  RefreshPageGoneAction,
  RefreshPageGoneReason,
};

/** Which entry point seeded the job. */
export const REFRESH_MODE_LABEL: Record<RefreshMode, string> = {
  scheduled: "Scheduled",
  dup: "Duplicate",
};

/** One line each, because "why am I looking at this job" is the mode's whole job. */
export const REFRESH_MODE_HINT: Record<RefreshMode, string> = {
  scheduled:
    "The nightly sweep re-fetched this event's own source page because its cadence came due.",
  dup: "Another site's page was caught as a duplicate of this published event, and carried different data.",
};

export function refreshModeLabel(value: string | null | undefined): string {
  return value && value in REFRESH_MODE_LABEL
    ? REFRESH_MODE_LABEL[value as RefreshMode]
    : (value ?? "—");
}

/**
 * The ten values `v_refresh_jobs.refresh_outcome` can take.
 *
 * `null` is not in here and is not "unknown": it means the job has not reached a verdict yet.
 * {@link refreshOutcomeLabel} renders that as "In flight".
 */
export const REFRESH_OUTCOME_LABEL: Record<RefreshOutcome, string> = {
  unchanged: "Unchanged",
  updated: "Updated",
  dismissed: "Dismissed",
  shadow: "Shadow",
  retired: "Retired",
  bad_fetch: "Bad fetch",
  not_listed: "Not listed",
  skipped: "Skipped",
  failed: "Failed",
  hitl: "Needs review",
};

export function refreshOutcomeLabel(value: string | null | undefined): string {
  if (!value) return "In flight";
  return value in REFRESH_OUTCOME_LABEL
    ? REFRESH_OUTCOME_LABEL[value as RefreshOutcome]
    : value;
}

/**
 * Badge classes per outcome.
 *
 * Three tiers, deliberately: `unchanged` is the boring healthy majority and reads muted;
 * `updated`/`hitl` are the two that mean something happened; `bad_fetch`/`not_listed`/`failed`
 * are the ones worth chasing. `shadow` is amber because it means nothing was written — a run of
 * them is the kill-switch still being on, not a healthy night.
 */
export const REFRESH_OUTCOME_CLASSES: Record<RefreshOutcome, string> = {
  unchanged: "bg-zinc-500/15 text-zinc-700 dark:text-zinc-300",
  updated: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
  dismissed: "bg-zinc-500/15 text-zinc-700 dark:text-zinc-300",
  shadow: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
  retired: "bg-zinc-500/15 text-zinc-700 dark:text-zinc-300",
  bad_fetch: "bg-orange-500/15 text-orange-700 dark:text-orange-300",
  not_listed: "bg-orange-500/15 text-orange-700 dark:text-orange-300",
  skipped: "bg-zinc-500/15 text-zinc-700 dark:text-zinc-300",
  failed: "bg-red-500/15 text-red-700 dark:text-red-300",
  hitl: "bg-rose-500/15 text-rose-700 dark:text-rose-300",
};

export function refreshOutcomeClasses(
  value: string | null | undefined,
): string {
  if (!value || !(value in REFRESH_OUTCOME_CLASSES)) {
    return "bg-sky-500/15 text-sky-700 dark:text-sky-300";
  }
  return REFRESH_OUTCOME_CLASSES[value as RefreshOutcome];
}

/** `{ value, label }` pairs for the outcome filter, in the tuple's order. */
export const REFRESH_OUTCOME_FILTER_OPTIONS: ReadonlyArray<{
  value: RefreshOutcome;
  label: string;
}> = REFRESH_OUTCOMES.map((value) => ({
  value,
  label: REFRESH_OUTCOME_LABEL[value],
}));

/** `{ value, label }` pairs for the mode filter. */
export const REFRESH_MODE_FILTER_OPTIONS: ReadonlyArray<{
  value: RefreshMode;
  label: string;
}> = REFRESH_MODES.map((value) => ({
  value,
  label: REFRESH_MODE_LABEL[value],
}));

/** The twelve change kinds, as a curator reads them. */
export const REFRESH_CHANGE_KIND_LABEL: Record<RefreshChangeKind, string> = {
  cancelled: "Cancellation",
  date: "Date & time",
  venue: "Venue",
  title: "Title",
  description: "Description",
  ticket_url: "Ticket link",
  poster: "Poster",
  lineup: "Lineup",
  // R108 names the direction, because the two are separate proposals a curator
  // sees side by side — one adding a tag, one retracting one — and "Tag" twice
  // would not say which row does what.
  tag_add: "Tag added",
  tag_remove: "Tag removed",
  // B (tina, 2026-09-29): written by refresh after a lineup change, never proposed —
  // they appear only under Already applied automatically.
  type: "Type",
  genres: "Genres",
};

export function refreshChangeKindLabel(
  value: string | null | undefined,
): string {
  return value && value in REFRESH_CHANGE_KIND_LABEL
    ? REFRESH_CHANGE_KIND_LABEL[value as RefreshChangeKind]
    : (value ?? "—");
}

/**
 * The three kinds that fire a Knock notification when accepted, and which one.
 *
 * Surfaced in the panel because accepting one of these is not just a data edit — it pushes to
 * every user who saved the event. The backend gates each workflow on `status == Complete`, so
 * this is what *would* fire, not a promise that it will.
 *
 * **A kind alone is no longer the whole answer.** Owner ruling R79 narrowed `date` to moves of
 * a day or more, so a surface that puts a sentence on screen about a push must ask
 * `refreshPushEffect` for the proposal it is rendering rather than reading this map directly.
 * The map stays the single list of kind → workflow, and `refreshPushEffect` is the only thing
 * entitled to say a push will happen.
 */
export const REFRESH_KIND_NOTIFICATION: Partial<
  Record<RefreshChangeKind, string>
> = {
  date: "DATE_CHANGED",
  venue: "VENUE_CHANGED",
  cancelled: "EVENT_CANCELLED",
};

/**
 * How far a `date` proposal has to move the start before accepting it pushes DATE_CHANGED.
 *
 * Owner ruling R79, 2026-09-09: "for a DATE_CHANGED to be fired, it should be at least a
 * 24hour difference." The live park behind it proposed `2026-09-20T17:30:00+00:00` →
 * `2026-09-20T19:00:00+02:00` — 17:30Z against 17:00Z, the same evening 30 minutes apart — and
 * this panel still told the curator that accepting it would push to everyone who saved the
 * event. Of the 622 date proposals parked as of that date, 587 are inside this threshold, so
 * the sentence was wrong far more often than it was right.
 *
 * **Mirrors tina's `DATE_PUSH_MIN_DELTA`** in
 * `src/pipeline/handlers/music_event_refresh/domain/knock.py`, which is where the push is
 * actually suppressed. The duplication across the two repos is deliberate and named here so it
 * is visible rather than accidental: this copy decides only what IMS *promises*, so if tina's
 * number moves and this one does not, the panel goes back to lying — in whichever direction.
 *
 * Inclusive, because the owner said "at least": a move of exactly this much still pushes.
 */
export const DATE_PUSH_MIN_DELTA_MS = 24 * 60 * 60 * 1000;

/**
 * What accepting one proposal does to the people who saved the event.
 *
 * - `push` — a Knock workflow really goes out, and names it.
 * - `quiet` — the change applies and nobody is notified, carrying how far the start moves so
 *   the panel can say so instead of leaving the curator to infer it from silence.
 * - `none` — one of the five kinds that was never a push in the first place.
 */
export type RefreshPushEffect =
  | { effect: "push"; workflow: string }
  | { effect: "quiet"; deltaMs: number }
  | { effect: "none" };

/**
 * Whether accepting `proposal` notifies anyone, and what to say about it.
 *
 * `venue` and `cancelled` are unconditional: a venue move and a cancellation are always
 * material. R79 narrows `date` alone, and only for the push — the new start still applies
 * either way, which is why the `quiet` branch carries copy rather than nothing.
 *
 * An unresolvable instant on either side counts as a push, matching tina's deliberately loud
 * fallback: failing to warn people about a genuine reschedule is worse than one extra push.
 */
export function refreshPushEffect(
  proposal: Pick<ProposedChange, "kind" | "old" | "new">,
): RefreshPushEffect {
  const workflow = REFRESH_KIND_NOTIFICATION[proposal.kind];
  if (workflow === undefined) return { effect: "none" };
  if (proposal.kind !== "date") return { effect: "push", workflow };
  const deltaMs = startDeltaMs(proposal.old, proposal.new);
  if (deltaMs === null || deltaMs >= DATE_PUSH_MIN_DELTA_MS) {
    return { effect: "push", workflow };
  }
  return { effect: "quiet", deltaMs };
}

/**
 * The line under a `date` proposal whose push R79 suppresses.
 *
 * Says all three things the curator needs in one sentence — that the change still applies,
 * that nobody hears about it, and how far the start actually moves — because "no warning" and
 * "warning missing" look identical on screen.
 */
export function refreshQuietDateNotice(deltaMs: number): string {
  const minutes = Math.round(Math.abs(deltaMs) / 60_000);
  const moved = minutes === 0 ? "under a minute" : humanMinutes(minutes);
  return `Accepting this applies the new start without notifying anyone: it moves by ${moved}, and a DATE_CHANGED push needs at least 24 hours.`;
}

/**
 * "45 minutes", "1 hour 30 minutes", "3 days", "1 day 2 hours".
 *
 * The two most significant non-zero units of days/hours/minutes, never three: at day scale
 * the minutes are noise, and below a day this is hours-and-minutes, which is exactly what
 * {@link refreshQuietDateNotice} has always printed — that caller only ever passes a sub-day
 * delta, since R79's threshold is what put it on the quiet branch in the first place.
 */
function humanMinutes(minutes: number): string {
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const rest = minutes % 60;
  const parts: string[] = [];
  if (days > 0) parts.push(`${days} ${days === 1 ? "day" : "days"}`);
  if (hours > 0) parts.push(`${hours} ${hours === 1 ? "hour" : "hours"}`);
  if (rest > 0) parts.push(`${rest} ${rest === 1 ? "minute" : "minutes"}`);
  return parts.slice(0, 2).join(" ");
}

/**
 * How far apart two starts are in **signed** milliseconds — positive when `next` is later —
 * or `null` when either side is not a resolvable instant.
 *
 * **Instants, never wall-clock strings or calendar days.** The two sides of a real `date`
 * proposal routinely carry different UTC offsets — the park behind R79 described nearly the
 * same moment twice — so a string or same-day comparison gets this exactly backwards.
 *
 * An offset-LESS value counts as unresolvable rather than being guessed at. All 622 date
 * proposals parked as of 2026-09-09 carry an explicit offset, so nothing real is lost, and
 * `Date.parse` would read it in the curator's own browser timezone, scoring the same park
 * differently in Athens and in Los Angeles.
 *
 * R80 gave the panel the published event's timezone, and it is deliberately still not applied
 * here. That zone is the frame a *resolved* instant is displayed in — it is not evidence about
 * what an offset-less string on some page meant, which is a question only tina's own
 * `resolve_start_utc` has the context to answer. So this stays `null` and
 * {@link refreshDateComparison} declines to normalise, rather than either of them inventing a
 * reading.
 *
 * The sign is what {@link refreshDateComparison} needs to say "earlier" or "later" (owner
 * ruling R80); {@link startDeltaMs} is the same number without it, which is what R79's push
 * threshold needs. One subtraction, two readings — deliberately not two calculations.
 */
export function startDeltaSignedMs(
  old: string | null | undefined,
  next: string | null | undefined,
): number | null {
  const from = parseInstant(old);
  const to = parseInstant(next);
  if (from === null || to === null) return null;
  return to - from;
}

/**
 * How far apart two starts are in **absolute** milliseconds, or `null` when either side is
 * not a resolvable instant.
 *
 * Absolute on purpose: for the push decision, 30 minutes earlier is as immaterial as 30
 * minutes later. See {@link startDeltaSignedMs} for the direction and for why an offset-less
 * value is unresolvable rather than guessed at.
 */
export function startDeltaMs(
  old: string | null | undefined,
  next: string | null | undefined,
): number | null {
  const signed = startDeltaSignedMs(old, next);
  return signed === null ? null : Math.abs(signed);
}

/** An ISO 8601 timestamp carrying an offset, as epoch ms — `null` for anything else. */
function parseInstant(value: string | null | undefined): number | null {
  const raw = (value ?? "").trim();
  if (!/(?:Z|[+-]\d{2}:?\d{2})$/.test(raw)) return null;
  const ms = Date.parse(raw);
  return Number.isNaN(ms) ? null : ms;
}

/** The zone both sides of a date comparison fall back to when the park names none. */
export const REFRESH_DATE_FALLBACK_ZONE = "UTC";

/**
 * One `date` proposal, with both sides on one clock and the gap in words (owner ruling R80).
 *
 * `raw-only` is not an error state to hide — it is the honest answer when one side cannot be
 * placed on a clock at all, and the panel still shows the stored strings underneath either
 * way.
 */
export type RefreshDateComparison =
  | {
      status: "normalised";
      /** The published start, read in `zone`, e.g. `Sun 20 Sep 2026, 19:30`. */
      from: string;
      /** The proposed start, read in the same `zone`. */
      to: string;
      /**
       * The proposed end, read in the same `zone` — a takeover's `new_end`. `null` when the
       * proposal carries none, and also when it carries one that is not an exact instant, in
       * which case the caller still has the stored string to show.
       */
      end: string | null;
      /** `30 minutes earlier`, `3 days later`, or the same-instant phrasing. */
      delta: string;
      /** The IANA zone both sides were read in — the event's own, or the UTC fallback. */
      zone: string;
      /** True when `zone` is the fallback rather than the event's own. */
      zoneIsFallback: boolean;
      /** The clause naming the frame, and saying so when it is a fallback. */
      frame: string;
    }
  | {
      status: "raw-only";
      /** Which side could not be read, and that the comparison was not normalised. */
      note: string;
      /** Why the browser's own timezone is not used as a stand-in. */
      detail: string;
    };

/**
 * Put both sides of a `date` proposal on the event's own clock, and name the gap in words.
 *
 * **The contradiction this closes.** Mood stores `startDate` in UTC, and
 * `refresh_compare.j2` instructs the model to answer in the *page's* local offset — so the two
 * strings on a `date` proposal are in two different offset conventions by construction. This
 * panel used to print them raw, side by side, which is how the park behind R79 and R80,
 * `2026-09-20T17:30:00+00:00` → `2026-09-20T19:00:00+02:00`, read on screen as a jump from
 * 17:30 to 19:00. It is a 30-minute correction: 19:30 → 19:00 in Europe/Berlin, the event's
 * own zone. A real correction and a pure offset restatement were the same pixels.
 *
 * **Why the event's timezone and not the curator's.** `Intl` would happily render both sides
 * in whatever zone the browser is set to, and the answer would then differ between an Athens
 * curator and a Los Angeles one — for the *same* park, on the *same* day. That is the trap
 * {@link startDeltaSignedMs} already refuses for the delta, and it is refused here too: the
 * frame is the event's zone, off `readRefreshPublishedSummary`, or an explicitly labelled UTC
 * when the park carries none.
 *
 * **Never a guess.** An offset-less or unparseable side returns `raw-only`, saying which side
 * and why, because a normalised line built on an assumed zone is worse than no normalised line
 * — it looks authoritative and is wrong by however far the assumption is off.
 */
export function refreshDateComparison(
  proposal: Pick<ProposedChange, "old" | "new" | "new_end">,
  timezone: string | null | undefined,
): RefreshDateComparison {
  const from = parseInstant(proposal.old);
  const to = parseInstant(proposal.new);
  if (from === null || to === null) {
    return rawOnly(proposal.old, from !== null, to !== null);
  }

  const { zone, zoneIsFallback, frame } = dateFrame(timezone);
  // The end goes through the same `parseInstant` and `formatInZone` as the start, in the same
  // zone, so the two cannot read on different clocks.
  const end = parseInstant(proposal.new_end);
  return {
    status: "normalised",
    from: formatInZone(from, zone),
    to: formatInZone(to, zone),
    end: end === null ? null : formatInZone(end, zone),
    delta: humanGap(to - from),
    zone,
    zoneIsFallback,
    frame,
  };
}

/**
 * ONE stored timestamp, read on the event's own clock — the single-sided
 * {@link refreshDateComparison}.
 *
 * **The contradiction this closes is the same one, one panel up.** Mood stores `startDate`
 * in UTC, so `RefreshContextView`'s "Published" row printed `2026-09-12T04:00:00+00:00`
 * beside the label `event timezone America/Los_Angeles` — the zone named and then not
 * applied. The same event opened in `event/edit` reads `September 11th, 2026 21:00:00`,
 * because that screen does apply it. Both are the same instant; a curator deciding "did
 * the date change?" saw two different DAYS for one event and had to do the offset
 * arithmetic in their head to find out they agreed. Measured on 2026-09-12: every one of
 * the 856 parks written so far stores a UTC-offset start against a non-UTC event zone, and
 * of the 171 still open, 169 printed the wrong clock time and 40 the wrong calendar day.
 *
 * **Composed, never reimplemented.** `parseInstant` decides what is readable, {@link
 * resolveFrameZone} decides the zone and says why when it falls back, and `formatInZone`
 * does the arithmetic and supplies the words. This function only joins them, so the
 * published start and a `date` proposal cannot drift into two renderings of one instant —
 * which is the whole failure being fixed.
 *
 * **Never a guess**, exactly as on the two-sided path: an offset-less or unparseable value
 * returns `raw-only` and the caller keeps showing the stored string. A normalised line
 * built on an assumed zone looks authoritative and is wrong by however far the assumption
 * is off.
 */
export type RefreshInstantDisplay =
  | {
      status: "normalised";
      /** The instant read in `zone`, e.g. `Fri 11 Sep 2026, 21:00`. */
      text: string;
      /** The IANA zone it was read in — the event's own, or the UTC fallback. */
      zone: string;
      /** True when `zone` is the fallback rather than the event's own. */
      zoneIsFallback: boolean;
      /** The clause naming the frame, and saying so when it is a fallback. */
      frame: string;
    }
  | {
      status: "raw-only";
      /** That the value could not be placed on a clock, so nothing was normalised. */
      note: string;
      /** Why the browser's own timezone is not used as a stand-in. */
      detail: string;
    };

export function refreshInstantDisplay(
  value: string | null | undefined,
  timezone: string | null | undefined,
): RefreshInstantDisplay {
  const ms = parseInstant(value);
  if (ms === null) {
    return {
      status: "raw-only",
      note: !(value ?? "").trim()
        ? "The published event has no stored start."
        : "This value cannot be read as an exact instant — no UTC offset, or not a timestamp at all — so it could not be shown on the event's own clock.",
      detail: RAW_ONLY_DETAIL,
    };
  }
  const { zone, zoneIsFallback, qualifier } = resolveFrameZone(timezone);
  return {
    status: "normalised",
    text: formatInZone(ms, zone),
    zone,
    zoneIsFallback,
    frame: `read in ${zone}${qualifier}`,
  };
}

/** The gap in words, signed: positive `deltaMs` means the proposed start is later. */
function humanGap(deltaMs: number): string {
  const minutes = Math.round(Math.abs(deltaMs) / 60_000);
  // A `date` proposal whose two sides are the same instant is an offset restatement and
  // nothing else. Tina's `drop_same_instant_date_changes` catches these with a 60s tolerance,
  // so one reaching a curator means that guard is off or the park predates it — either way
  // "0 minutes later" reads like a bug where "the same moment" reads like the answer.
  if (minutes === 0) return "the same moment, written two ways";
  return `${humanMinutes(minutes)} ${deltaMs < 0 ? "earlier" : "later"}`;
}

/**
 * Which zone to read both sides in, and the clause that names it.
 *
 * A zone the runtime cannot resolve is treated exactly like a missing one, and said out loud
 * rather than swallowed: `Intl.DateTimeFormat` throws a `RangeError` on an unknown `timeZone`,
 * and a comparison panel that throws inside a table cell is worse than one that degrades.
 */
function dateFrame(timezone: string | null | undefined): {
  zone: string;
  zoneIsFallback: boolean;
  frame: string;
} {
  const { zone, zoneIsFallback, qualifier } = resolveFrameZone(timezone);
  return { zone, zoneIsFallback, frame: `both read in ${zone}${qualifier}` };
}

/**
 * Which zone to read in, and the clause that qualifies it — without the lead.
 *
 * Split out of {@link dateFrame} so {@link refreshInstantDisplay} can say "read in X"
 * about ONE timestamp while this says "both read in X" about two, off the same decision
 * and the same words. The qualifier is the half that carries the reasoning, and it is the
 * half that must not fork: a curator who sees "because this park carries no event
 * timezone" under a proposal and a different excuse under the published start would have
 * to work out whether they are the same fallback.
 */
function resolveFrameZone(timezone: string | null | undefined): {
  zone: string;
  zoneIsFallback: boolean;
  qualifier: string;
} {
  const named = (timezone ?? "").trim();
  if (!named) {
    return {
      zone: REFRESH_DATE_FALLBACK_ZONE,
      zoneIsFallback: true,
      qualifier: ", because this park carries no event timezone",
    };
  }
  if (!isResolvableZone(named)) {
    return {
      zone: REFRESH_DATE_FALLBACK_ZONE,
      zoneIsFallback: true,
      qualifier: `, because the event timezone ${named} is not one this browser can resolve`,
    };
  }
  return {
    zone: named,
    zoneIsFallback: false,
    qualifier: ", the event's own timezone",
  };
}

function isResolvableZone(zone: string): boolean {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

/** Fixed names, because a comparison panel's own labels are not the browser's to choose. */
const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;
const WEEKDAY_NAMES = [
  "Sun",
  "Mon",
  "Tue",
  "Wed",
  "Thu",
  "Fri",
  "Sat",
] as const;

/**
 * An instant as `Sun 20 Sep 2026, 19:30`, read in `zone`.
 *
 * **`Intl` is used only for the arithmetic, never for the words.** It is the only thing that
 * knows what `2026-09-20T17:30:00Z` reads as in `Europe/Berlin`, DST included, so it supplies
 * the civil year/month/day/hour/minute — and nothing else. The month and weekday names come
 * from the tables above and the layout from this template, because every locale-supplied part
 * of a date string is a way for the same park to read differently in two offices: `en-GB`'s
 * September abbreviation is `Sep` on ICU 71 and `Sept` on ICU 77, `en-US` reorders the day and
 * the month, and a non-Latin numbering locale would change the digits. Pinning a locale fixes
 * the first two and not the third; not asking a locale for words fixes all of them.
 *
 * The weekday is derived rather than requested for the same reason: the civil date is already
 * known, and a UTC `Date` built from those three numbers has exactly one day-of-week.
 *
 * `hourCycle: "h23"` rather than `hour12: false`: midnight has to read `00:00`, and those two
 * spellings do not agree about that across engines.
 */
function formatInZone(ms: number, zone: string): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZone: zone,
  }).formatToParts(new Date(ms));
  const at = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((part) => part.type === type)?.value ?? "";

  const year = Number(at("year"));
  const month = Number(at("month"));
  const day = Number(at("day"));
  const weekday =
    WEEKDAY_NAMES[new Date(Date.UTC(year, month - 1, day)).getUTCDay()];
  return `${weekday} ${day} ${MONTH_NAMES[month - 1]} ${year}, ${at("hour")}:${at("minute")}`;
}

/**
 * Why a value that could not be placed on a clock is left exactly as stored.
 *
 * One sentence, one home: {@link rawOnly} and {@link refreshInstantDisplay} both degrade
 * for the same reason and must not explain it two ways.
 */
const RAW_ONLY_DETAIL =
  "The stored strings below are shown exactly as they are stored — reading them in your own timezone would answer differently in Athens and in Los Angeles.";

/** The `raw-only` verdict, naming the side that could not be read. */
function rawOnly(
  old: string | null | undefined,
  fromOk: boolean,
  toOk: boolean,
): RefreshDateComparison {
  const detail = RAW_ONLY_DETAIL;
  if (!(old ?? "").trim()) {
    return {
      status: "raw-only",
      note: "The published event has no stored start, so there is nothing to compare this against.",
      detail,
    };
  }
  const side =
    !fromOk && !toOk
      ? "Neither value"
      : !fromOk
        ? "The published value"
        : "The proposed value";
  return {
    status: "raw-only",
    note: `${side} cannot be read as an exact instant — no UTC offset, or not a timestamp at all — so this comparison could not be normalised.`,
    detail,
  };
}

/**
 * What each page-gone action does, in the curator's terms.
 *
 * Order is the order they are offered: the destructive one first because it is the answer when
 * the evidence is good, then the two that keep the event live.
 */
export const REFRESH_PAGE_GONE_ACTION_LABEL: Record<
  RefreshPageGoneAction,
  string
> = {
  cancel: "Cancel the event",
  retire: "Stop refreshing it",
  dismiss: "Leave it alone",
};

export const REFRESH_PAGE_GONE_ACTION_HINT: Record<
  RefreshPageGoneAction,
  string
> = {
  cancel:
    "Marks the published event cancelled and notifies everyone who saved it. Use it when the page really is gone for good.",
  retire:
    "Keeps the event exactly as it is and never refreshes it again. Use it when the source moved but the event is fine.",
  dismiss:
    "Resets the strike counter and keeps refreshing. Use it when the misses look like an outage.",
};

/**
 * Why tina called the page gone, in the curator's terms.
 *
 * The panel used to print `page_gone_reason` raw, so all 29 parks open the day this
 * shipped read "focal_missing" where the explanation should be. The codes are not
 * equally alarming and the answer differs per code — a `bad_fetch` streak is usually an
 * outage worth dismissing, a `same_event_false` usually is not — so they are spelled out
 * rather than passed through.
 */
export const REFRESH_PAGE_GONE_REASON_LABEL: Record<
  RefreshPageGoneReason,
  string
> = {
  bad_fetch: "Three fetches in a row failed",
  not_listed: "Dropped off the site's own listing three nights running",
  unreadable: "Tina could not read the page for four nights",
  blocked: "The site blocked Tina — the page may still be live",
  redirected: "The address now opens a list page",
  focal_missing: "This date is no longer on the page",
  same_event_false: "The page is about a different event now",
};

/** One line each on what the code actually means for the decision below it. */
export const REFRESH_PAGE_GONE_REASON_HINT: Record<
  RefreshPageGoneReason,
  string
> = {
  bad_fetch:
    "Tina could not read the page at all on three consecutive nights — a 404, a block, or a timeout. Often an outage rather than a dead event.",
  not_listed:
    "The site's listing page, which is where tina finds this event, stopped carrying it on three consecutive nights.",
  unreadable:
    "Every read came back empty or failed, four nights running. That says nothing about the event — open the page yourself before you cancel.",
  blocked:
    "Every reader, including a real browser, met a bot check or an access-denied page. That is the site blocking Tina, not proof the event is gone — open the page yourself before you cancel.",
  redirected:
    "The event's link redirects to a listing; Tina never saw the event page. Retire or dismiss — do not cancel on this.",
  focal_missing:
    "Tina read the page fine, but the specific date Mood publishes was not on it — the page may have moved on to the next occurrence.",
  same_event_false:
    "The compare model read the page and said it is no longer about this event at all.",
};

/**
 * The label for a `page_gone_reason`, or the raw string for a code this build predates.
 * `null` for a park that carries no reason — the panel falls back to its own copy.
 */
export function refreshPageGoneReasonLabel(
  value: string | null | undefined,
): string | null {
  if (!value) return null;
  return value in REFRESH_PAGE_GONE_REASON_LABEL
    ? REFRESH_PAGE_GONE_REASON_LABEL[value as RefreshPageGoneReason]
    : value;
}

/** The hint for a known code; `null` for an unknown one, so nothing is invented. */
export function refreshPageGoneReasonHint(
  value: string | null | undefined,
): string | null {
  return value && value in REFRESH_PAGE_GONE_REASON_HINT
    ? REFRESH_PAGE_GONE_REASON_HINT[value as RefreshPageGoneReason]
    : null;
}

/**
 * Where a curator goes to look at the published Mood event.
 *
 * Built here rather than read off `problem.ims_url`, which tina stamps as
 * `…/events/show/{id}` (`stages/apply.py::_ims_url`): `events` declares a `show` path on the
 * Refine resource but `App.tsx` mounts only `edit/:id`, `create` and `enrichment/:id`, so that
 * URL 404s. It is the same trap `enrichment-jobs.ts`'s `ENTITY_HREFS` documents for
 * artists/venues. Reported upstream; until it changes, ignore `ims_url` for navigation.
 */
export function moodEventHref(
  adminEventId: string | null | undefined,
): string | null {
  return adminEventId ? `/events/edit/${adminEventId}` : null;
}

/** The `refresh_review` problem, or null for every other reason. */
export function refreshProblemOf(
  context: HitlContext | null | undefined,
): RefreshReviewProblem | null {
  const problem = context?.problem;
  if (!problem || problem.reason !== "refresh_review") return null;
  return problem;
}

/**
 * What the compare model concluded about the page as a whole — the one piece of the
 * model's own words that `RefreshReviewProblem` does **not** carry.
 *
 * `ProposedChange.evidence` is a verbatim page quote per proposal; `reasoning` is the
 * model's sentence about the page: why it is (or is not) the same event, what it found,
 * what it did not. On a `page_gone` park it is the ONLY reasoning that exists at all —
 * `proposals` is empty by design there, so without this a curator deciding whether to
 * cancel a published event had nothing but a four-word code to go on.
 *
 * It lives in `hitl_context.stage_outputs.refresh_compare`, which
 * `build_hitl_context()` deep-copies off `ctx.previous_outputs` — untyped jsonb, hence a
 * `read*` projection through the same value-readers `readRefreshJobRef` uses. Verified
 * present on all 1,113 parks open on 2026-09-07; still every field is optional, because
 * the shape is a stage output and not a contract.
 */
export interface RefreshCompareVerdict {
  /** The model's own sentence about the page. */
  reasoning: string | null;
  /** Did the page still describe the published event? `page_gone` is `mode=scheduled && !same_event`. */
  same_event: boolean | null;
  same_event_confidence: number | null;
  /** Was the exact published date on the page (R33's focal instance)? */
  focal_instance_found: boolean | null;
  /** Did the page carry OTHER dates for the same act — usually a tour page that moved on. */
  other_instances_on_page: boolean | null;
}

/** The compare verdict off a `refresh_review` context, or null when there is none to read. */
export function readRefreshCompareVerdict(
  context: HitlContext | null | undefined,
): RefreshCompareVerdict | null {
  if (!refreshProblemOf(context)) return null;
  const compare = asRecord(asRecord(context?.stage_outputs).refresh_compare);
  if (Object.keys(compare).length === 0) return null;
  return {
    reasoning: asString(compare.reasoning) ?? null,
    same_event: asBoolean(compare.same_event) ?? null,
    same_event_confidence: numberOrNull(compare.same_event_confidence),
    focal_instance_found: asBoolean(compare.focal_instance_found) ?? null,
    other_instances_on_page: asBoolean(compare.other_instances_on_page) ?? null,
  };
}

/**
 * The live Mood event the refresh is about, as the compare stage saw it.
 *
 * The item show page's header identifies the row by its Tina id, its run and its site —
 * true for a scrape row, useless for a refresh job, whose subject is a *published* event
 * with a name. That name is already on the park, inside the compare stage's
 * `PublishedEventView` dump; this projects the handful of fields a curator needs to know
 * which event they are about to change, without a Mood read.
 */
export interface RefreshPublishedSummary {
  name: string | null;
  /** UTC ISO 8601, as Mood stores it. `timezone` is the event's own zone. */
  start_date: string | null;
  timezone: string | null;
  venue_name: string | null;
  venue_city: string | null;
  /**
   * The published event's own Google Maps link, or null when it has none.
   *
   * **Not the same thing as `venue_name`.** A venue PROPOSAL is raised because the
   * published event has no venue record, so `venue_name` is null there while this is
   * not — the event still carries a pin. That pin is the only current location a
   * curator can compare the proposed venue against, which is why
   * `refresh-resolution.tsx`'s `VenueComparison` reads it instead of printing "(empty)".
   *
   * Two shapes exist on live data (`https://maps.google.com/?cid=…` and a long
   * `/maps/place/…@lat,lng…` URL), so it is rendered as a link and a consumer must
   * never try to read coordinates out of it.
   */
  maps_url: string | null;
  /**
   * The published event's own pin as coordinates, or null when it carries none.
   *
   * The one location field a `venue` proposal can rely on. `venue_city` is read off
   * the venue's geodata and a venue proposal exists BECAUSE the event has no venue,
   * so it is null on every open venue proposal (71 of 71 on 2026-09-11) while this
   * is set on all of them. `RefreshResolution` turns it into the nearest city for
   * `VenueActionFields`'s "use this city's center" autofill.
   *
   * Read it from HERE and never out of `maps_url`'s text — see that field.
   */
  geolocation: { lat: number; lng: number } | null;
  lineup_names: string[];
  /** `Complete`, `Draft`, … — a refresh only ever runs against a published event. */
  status: string | null;
}

export function readRefreshPublishedSummary(
  context: HitlContext | null | undefined,
): RefreshPublishedSummary | null {
  if (!refreshProblemOf(context)) return null;
  const published = asRecord(
    asRecord(asRecord(context?.stage_outputs).refresh_compare).published,
  );
  const name = asString(published.name);
  // Nameless means "there is no published view here", not "an event with no name":
  // `PublishedEventView.name` is non-optional on tina's side.
  if (!name) return null;
  return {
    name,
    start_date: asString(published.start_date) ?? null,
    timezone: asString(published.timezone) ?? null,
    venue_name: asString(published.venue_name) ?? null,
    venue_city: asString(published.venue_city) ?? null,
    maps_url: asString(published.maps_url) ?? null,
    geolocation: readGeolocation(published.geolocation),
    lineup_names: asStringArray(published.lineup_names),
    status: asString(published.status) ?? null,
  };
}

/** `{latitude, longitude}` as tina dumps it, as the `{lat, lng}` the map helpers use. */
function readGeolocation(raw: unknown): { lat: number; lng: number } | null {
  const geo = asRecord(raw);
  const lat = numberOrNull(geo.latitude);
  const lng = numberOrNull(geo.longitude);
  if (lat === null || lng === null) return null;
  return { lat, lng };
}

/**
 * `events.metadata.refresh` — tina's `RefreshJobRef`, present on every refresh job row from the
 * moment the sweep or the duplicate hook seeds it.
 *
 * snake_case, verbatim off the wire, for the reason `enrichment-jobs.ts` gives: `v_refresh_jobs`
 * names its columns the same way, so a reader can diff this against either without a
 * translation table.
 *
 * `proposals`/`applied` are the *stored* record of what the compare stage found and what
 * `refresh_apply` wrote — `v_refresh_jobs` counts the same two arrays for its
 * `proposal_count`/`applied_count`, so the item panel and the Refreshes tab cannot disagree.
 */
export interface RefreshJobRef {
  mode: RefreshMode | null;
  admin_event_id: string;
  /** The Tina row this refresh is about: the publishing scrape row, or the duplicate in `dup` mode. */
  source_event_id: string | null;
  /** `gs://` ref of the previous good fetch output — the other half of the diff. */
  prev_fetch_output_ref: string | null;
  /** `dup` mode only. */
  dup_confidence: number | null;
  dup_stage: string | null;
  /** A `discover_carry` site's list no longer carries this event — a page-gone strike. */
  not_listed: boolean;
  proposals: ProposedChange[];
  applied: RefreshChangeKind[];
}

/**
 * Read `metadata.refresh` off a job row. Returns `null` when the row is not a refresh job, or
 * when the block is missing the one field that makes it addressable (`admin_event_id`).
 *
 * `content_type` is the authority on what kind of row this is; the metadata block is trusted only
 * for the detail. That matters more here than for enrichment: the nightly `discover` seed for a
 * carry site is also `music_event_refresh` but carries `metadata.refresh_discover` and no
 * `metadata.refresh`, so it correctly reads back as `null` — the same exclusion
 * `v_refresh_jobs`' `metadata ? 'refresh'` predicate makes.
 */
export function readRefreshJobRef(item: PipelineItem): RefreshJobRef | null {
  if (!isRefreshContentType(item.content_type)) return null;
  const block = asRecord(asRecord(item.metadata).refresh);
  const adminEventId = asString(block.admin_event_id);
  if (!adminEventId) return null;

  const mode = asString(block.mode);
  return {
    mode:
      mode && (REFRESH_MODES as readonly string[]).includes(mode)
        ? (mode as RefreshMode)
        : null,
    admin_event_id: adminEventId,
    source_event_id: asString(block.source_event_id) ?? null,
    prev_fetch_output_ref: asString(block.prev_fetch_output_ref) ?? null,
    dup_confidence: numberOrNull(block.dup_confidence),
    dup_stage: asString(block.dup_stage) ?? null,
    not_listed: asBoolean(block.not_listed) ?? false,
    proposals: readProposals(block.proposals),
    applied: readChangeKinds(block.applied),
  };
}

/**
 * `metadata.refresh_page_gone` / `metadata.refresh_page_gone_reason` — top-level keys, not nested
 * under `metadata.refresh`.
 *
 * Top-level because `merge_event_jsonb` is a shallow Postgres merge: writing them inside the
 * `refresh` block would replace the whole `RefreshJobRef`. `refresh_apply` stamps them on every
 * finished path, including shadow mode, where the job never raises a HITL — which is exactly when
 * this is the only place the page-gone signal survives.
 */
export interface RefreshPageGoneBlock {
  page_gone: boolean;
  reason: string | null;
}

export function readRefreshPageGone(item: PipelineItem): RefreshPageGoneBlock {
  const metadata = asRecord(item.metadata);
  return {
    page_gone: asBoolean(metadata.refresh_page_gone) ?? false,
    reason: asString(metadata.refresh_page_gone_reason) ?? null,
  };
}

/** Proposals off an untyped array, dropping anything without the two fields that render. */
function readProposals(raw: unknown): ProposedChange[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry) => {
    const record = asRecord(entry);
    const kind = asString(record.kind);
    const next = asString(record.new);
    if (!kind || !next) return [];
    if (!(REFRESH_CHANGE_KINDS as readonly string[]).includes(kind)) return [];
    return [
      {
        kind: kind as RefreshChangeKind,
        old: asString(record.old) ?? null,
        new: next,
        confidence: numberOrNull(record.confidence) ?? 0,
        evidence: asString(record.evidence) ?? "",
      },
    ];
  });
}

function readChangeKinds(raw: unknown): RefreshChangeKind[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry) => {
    const kind = asString(entry);
    return kind && (REFRESH_CHANGE_KINDS as readonly string[]).includes(kind)
      ? [kind as RefreshChangeKind]
      : [];
  });
}

function numberOrNull(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}
